import React from 'react'
import { array, bool, string, shape, number } from 'prop-types'
import styled from 'styled-components'
import { Box } from 'rebass'
import { rem } from 'polished'
import { Image, Heading, Text } from 'rebass'
import NumberFormat from 'react-number-format'
import citygateTheme from '../../theme/'
import LoadingSpinner from './components/LoadingSpinner'

import kia from '../../images/kia.png'
import vw from '../../images/volkswagen-financial-services.png'
import blackhorse from '../../images/blackhorse.png'

import { Panel, PanelHeader, PanelFooter, Container } from '../../utils/style'

const brandMap = {
  volkswagen: { image: vw, url: 'https://volkswagen.com' },
  kia: { image: kia, url: 'https://kia.com' },
  blackhorse: { image: blackhorse, url: 'https://blackhorse.com' }
}

const LendersPanel = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
  transform: scale(0.7);
  max-width: 550px;
  margin: 0 auto;
`

const LenderImage = styled(Image)`
  margin: 3px 6px !important;
`

const PanelFrame = styled(Panel)`
  width: 100%;
  border-radius: 0;
  border: 0;
  background-color: ${(props) => props.theme.colors.blue};
  text-align: center;
  font-size: ${(props) => rem(props.theme.fontSizes[2])};
  div > div {
    line-height: 1.5;
  }
`

const Header = styled(PanelHeader)`
  color: ${(props) => props.theme.colors.white};
  border: 0;
  text-transform: uppercase;
  font-weight: bold;
  padding-top: 24px;
`

const Footer = styled(PanelFooter)`
  color: ${(props) => props.theme.colors.white_op65};
  border: 0;
  text-transform: uppercase;
  img {
    margin: 0 auto;
  }
`

const FooterLink = styled.a`
  color: ${(props) => props.theme.colors.white};
  text-transform: none;

  &:hover {
    color: ${(props) => props.theme.colors.white_op65};
  }
`

const SummaryTable = styled.table`
  background-color: ${(props) => props.theme.colors.white};
  font-size: ${(props) => rem(props.theme.fontSizes[1])};
  font-weight: ${(props) => props.theme.weights[1]};
  color: ${(props) => props.theme.colors.grey_light};
  margin-bottom: 0;
  td {
    text-align: left;
    border: 0;
    padding: ${rem(5)} 5% !important;
  }
  td + td {
    text-align: right;
    color: ${(props) => props.theme.colors.blue};
    font-size: ${(props) => rem(props.theme.fontSizes[3])};
    white-space: nowrap;
  }
  tr:nth-child(odd) {
    background: ${(props) => props.theme.colors.blue_light};
  }
  tr:last-child td {
    padding-bottom: 8px;
  }
`

const SummarySupplement = styled.div`
  font-weight: ${(props) => props.theme.weights[0]};
  font-size: ${(props) => rem(props.theme.fontSizes[1])};
  margin-top: -13px;
`

class ContentFinanceSummary extends React.PureComponent {
  render() {
    const { lenders, active, error, summarylist, loading, contentid } =
      this.props
    return (
      <div>
        <PanelFrame>
          <Header pb={2}>{contentid} Representative Example</Header>
          {loading && (
            <Box p={16} style={{ height: 52 }}>
              <LoadingSpinner />
            </Box>
          )}
          <Box p={16}>
            {!error && !active ? (
              <Container
                bg="white"
                color={citygateTheme.colors.grey_light}
                px={'15%'}
                py={80}
              >
                <Heading f={4} mb={10} color={citygateTheme.colors.blue}>
                  Use the sliders to generate a quote
                </Heading>
              </Container>
            ) : error && active ? (
              <Container
                bg="white"
                color={citygateTheme.colors.grey_light}
                px={'15%'}
                py={80}
              >
                <Heading f={4} mb={10} color={citygateTheme.colors.blue}>
                  Unfortunately, we&#39;re unable to generate a quote
                </Heading>
                <Text f={3}>
                  To explore your finance options call us on
                  {` ${this.props.phoneNumber}`}
                </Text>
              </Container>
            ) : (
              active &&
              !error && (
                <SummaryTable>
                  <tbody>
                    {Object.keys(summarylist).map((key) => {
                      const item = summarylist[key]
                      return (
                        <tr
                          key={`cfins:${item.label}:${item.value}:${contentid}`}
                        >
                          <td>
                            {item.label}
                            {item.supplement &&
                              item.supplement.map((supplement) => (
                                <SummarySupplement
                                  key={`cfins:label:${item.label}:${item.value}:${supplement.label}:${supplement.value}:${contentid}`}
                                >
                                  {supplement.label}
                                </SummarySupplement>
                              ))}
                          </td>
                          <td>
                            <NumberFormat
                              value={item.value}
                              displayType={item.displayType || 'text'}
                              thousandSeparator
                              prefix={item.prefix}
                              suffix={item.suffix}
                            />
                            {item.supplement &&
                              item.supplement.map((supplement) => (
                                <SummarySupplement
                                  key={`cfins:nf:${item.label}:${item.value}:${supplement.label}:${supplement.value}:${contentid}`}
                                >
                                  <NumberFormat
                                    value={supplement.value}
                                    displayType={
                                      supplement.displayType || 'text'
                                    }
                                    thousandSeparator
                                    prefix={supplement.prefix}
                                    suffix={supplement.suffix}
                                  />
                                </SummarySupplement>
                              ))}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </SummaryTable>
              )
            )}
          </Box>
          <Footer pt={30} pb={30} px={24}>
            {(lenders.length > 1 && 'Our panel of lenders include:') ||
              `Quote via ${lenders[0].name}, come in store for more.`}
            <LendersPanel>
              {lenders.map((lender, i) => {
                return (
                  <a href={brandMap[lender.id].url} key={`${lender.id}-${i}`}>
                    <LenderImage
                      src={brandMap[lender.id].image}
                      alt={lender.id}
                    />
                  </a>
                )
              })}
            </LendersPanel>
            <Box mt={30}>
              <FooterLink href="/finance/disclosure" target="_blank">
                Finance Disclosure
              </FooterLink>
            </Box>
          </Footer>
        </PanelFrame>
      </div>
    )
  }
}

ContentFinanceSummary.defaultProps = {
  summarylist: {},
  contentid: 'hp',
  error: false,
  loading: false,
  lenders: [{ id: 'volkswagen' }, { id: 'blackhorse' }, { id: 'kia' }],
  phoneNumber: '0330 096 3851'
}
ContentFinanceSummary.propTypes = {
  summarylist: shape({
    label: string,
    value: number,
    prefix: string,
    suffix: string,
    supplement: array
  }),
  lenders: array,
  contentid: string,
  error: bool,
  phoneNumber: string,
  loading: bool
}
export default ContentFinanceSummary
